// "Forecasts you may be interested in": eight of Sayer's own recent forecasts
// on the new-question page, each opening as the user's own copy. Pure props.
import React, { useState } from "react";
import { C } from "../lib/colors.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { cardAnswer, cardMarket, questionUiKey } from "../lib/featured/featured.js";

// The grid answers to the hero's `.ask-hero-examples` container (AskHero's HERO_CSS).
const CSS = `
.sy-featured-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;}
@container (min-width: 780px){.sy-featured-grid{grid-template-columns:repeat(4,minmax(0,1fr));}}
@container (max-width: 520px){.sy-featured-grid{grid-template-columns:1fr;}}
.sy-featured-card{appearance:none;display:flex;flex-direction:column;justify-content:space-between;gap:12px;width:100%;min-width:0;text-align:left;font:inherit;cursor:pointer;background:var(--c-panel);border:1px solid var(--c-border);border-radius:12px;padding:12px 16px;color:var(--c-white);transition:border-color .12s ease,background .12s ease;}
.sy-featured-card:hover{border-color:var(--c-purpleDim);background:var(--c-rowInactive);}
.sy-featured-card:focus-visible{outline:2px solid var(--c-accent);outline-offset:2px;}
.sy-featured-card[data-related]{border-color:color-mix(in srgb,var(--c-accent) 55%,transparent);}
.sy-featured-slot{position:relative;display:grid;min-width:0;}
.sy-featured-slot>.sy-featured-card{animation:sy-featured-in .32s ease both;}
.sy-featured-leaving{position:absolute;inset:0;display:grid;pointer-events:none;animation:sy-featured-out .22s ease both;}
@keyframes sy-featured-in{from{opacity:0}to{opacity:1}}
@keyframes sy-featured-out{from{opacity:1}to{opacity:0}}
@media (prefers-reduced-motion: reduce){.sy-featured-slot>.sy-featured-card,.sy-featured-leaving{animation-duration:1ms;}}
`;

const clamp = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  minWidth: 0,
});

/**
 * One row of the comparison: who, their chance, the outcome it is for. The spaces between the
 * cells render nothing in the grid but keep the row's text readable as a phrase.
 */
function Row({ who, value, label, accent, ...attrs }) {
  return (
    <span {...attrs} style={{ display: "contents" }}>
      <span style={{ fontSize: 12, color: C.gray }}>{who}</span>{" "}
      <span
        className="sy-num"
        style={
          accent
            ? { fontSize: 18, fontWeight: 600, color: C.accent }
            : { fontSize: 14, fontWeight: 500, color: C.grayL }
        }
      >
        {value}
      </span>{" "}
      <span style={{ ...clamp(2), fontSize: accent ? 13 : 12, color: accent ? C.grayL : C.gray }}>
        {label}
      </span>
    </span>
  );
}

function FeaturedCard({ item, onOpen, now }) {
  const answer = cardAnswer(item);
  const market = cardMarket(item, { now });
  return (
    <button
      {...ui("ask-hero.featured", questionUiKey(item.questionKey))}
      type="button"
      className="sy-featured-card"
      data-related={item.related ? "" : undefined}
      data-telemetry-redact=""
      title={item.title}
      onClick={() => onOpen(item)}
    >
      <span style={{ ...clamp(3), fontSize: 13, lineHeight: 1.45, minHeight: "4.35em" }}>
        {item.title}
      </span>
      {(answer || market) && (
        <span
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto minmax(0, 1fr)",
            alignItems: "baseline",
            gap: "4px 8px",
            minWidth: 0,
            lineHeight: 1.45,
          }}
        >
          {answer && (
            <Row
              data-featured-answer={answer.kind}
              who="Sayer"
              value={answer.value}
              label={answer.label}
              accent
            />
          )}
          {market && (
            <Row
              data-featured-market=""
              title={market.title}
              who="Market"
              value={market.value}
              label={market.label}
            />
          )}
        </span>
      )}
    </button>
  );
}

/**
 * One place in the grid. When its question changes (the cards follow what is typed), the old card
 * fades out over the new one as that fades in.
 */
function Slot({ item, onOpen, now }) {
  const [shown, setShown] = useState(item);
  const [leaving, setLeaving] = useState(null);
  // Adjusting state while rendering (React's pattern for state derived from a changed prop):
  // no effect, so the new card and the fading one land in the same frame.
  if (item.questionKey !== shown.questionKey || item.related !== shown.related) {
    if (item.questionKey !== shown.questionKey) setLeaving(shown);
    setShown(item);
  }
  return (
    <div className="sy-featured-slot">
      <FeaturedCard key={shown.questionKey} item={shown} onOpen={onOpen} now={now} />
      {leaving && (
        <div
          key={`out-${leaving.questionKey}`}
          className="sy-featured-leaving"
          aria-hidden="true"
          inert
          onAnimationEnd={() => setLeaving(null)}
        >
          <FeaturedCard item={leaving} onOpen={() => {}} now={now} />
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object[]} props.items today's cards (lib/featured pickFeatured)
 * @param {(item: object, at: {slot: number}) => void} props.onOpen opens a card as the user's
 *   own copy; `slot` is its place on the page
 */
export default function FeaturedForecasts({ items, onOpen }) {
  const now = new Date();
  return (
    <div data-testid="featured-forecasts" {...surface("ask-hero.featured")}>
      <style>{CSS}</style>
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: C.white }}>
        Recent forecasts from Sayer. Open one to see the full details.
      </h2>
      <div className="sy-featured-grid" style={{ marginTop: 12 }}>
        {items.map((it, i) => (
          // Keyed by place, so a slot outlives the question it shows.
          <Slot key={i} item={it} onOpen={(item) => onOpen(item, { slot: i })} now={now} />
        ))}
      </div>
    </div>
  );
}
